import { atom } from 'jotai';
import { atomWithRefresh } from 'jotai/utils';

import { cookieJar } from '@consumer-frontend/nc-api';
import {
  AccountT,
  AlertBannerPropsT,
  ApplyEligibilityT,
  IncomeDataT,
  initialAlertBannerProps,
  initialModalConfig,
  IovationBlackboxT,
  ModalConfigT,
  PaymentDetailsT,
  RegistrationDataT,
  SEND_CODE_COUNTDOWN_SECONDS,
  TrackEventDescriptionPaymentT,
  TrackEventReqT,
  UnableToContinueReasonT,
} from '@consumer-frontend/nc-utils';
import { PageValuesT } from '@consumer-frontend/shared-ui';

export const accountIdAtom = atomWithRefresh(() => {
  const [accountId, error] = cookieJar.getAccountId();

  if (!accountId || error) {
    console.warn('Error getting account ID from cookie jar:', error, { accountId });

    return '';
  }

  return accountId;
});
accountIdAtom.debugLabel = 'accountIdAtom';

export const alertBannerAtom = atom<AlertBannerPropsT>(initialAlertBannerProps);
alertBannerAtom.debugLabel = 'alertBannerAtom';

export const messageCenterPaginationAtom = atom<PageValuesT>({
  limit: 10,
  offset: 0,
  total: 0,
});

export const messageDetailPaginationAtom = atom<PageValuesT>({
  limit: 1,
  offset: 0,
  total: 0,
});

export const showMobileNavAtom = atom(false);

export const modalConfigAtom = atom<ModalConfigT>(initialModalConfig);
modalConfigAtom.debugLabel = 'modalConfigAtom';

export const paymentDetailsAtom = atom<PaymentDetailsT | undefined>(undefined);
paymentDetailsAtom.debugLabel = 'paymentDetailsAtom';
export const paymentFailureDataAtom = atom<TrackEventReqT<TrackEventDescriptionPaymentT> | undefined>(undefined);
paymentFailureDataAtom.debugLabel = 'paymentFailureDataAtom';
export const hasFetchErrorAtom = atom<boolean>(false);
hasFetchErrorAtom.debugLabel = 'hasFetchErrorAtom';
export const requestedDrawAmountAtom = atom<number | undefined>(undefined);
requestedDrawAmountAtom.debugLabel = 'requestedDrawAmountAtom';
export const hasPreviewDepositErrorAtom = atom<boolean>(false);
hasPreviewDepositErrorAtom.debugLabel = 'hasPreviewDepositErrorAtom';

/* Apply App atoms */
export const accountAtom = atom<AccountT | undefined>();
accountAtom.debugLabel = 'accountAtom';
export const authMethodAtom = atom<'email' | 'oauth' | undefined>(undefined);
authMethodAtom.debugLabel = 'authMethodAtom';
export const isReturningCustomerFlowAtom = atom<boolean | undefined>(undefined);
isReturningCustomerFlowAtom.debugLabel = 'isReturningCustomerFlowAtom';

export const unableToContinueReasonAtom = atom<UnableToContinueReasonT>('existingEmail');
unableToContinueReasonAtom.debugLabel = 'unableToContinueReasonAtom';
export const doesPasswordExistAtom = atom<boolean | undefined>(undefined);
doesPasswordExistAtom.debugLabel = 'doesPasswordExistAtom';
export const isEmailApplyEligibleAtom = atom<boolean | undefined>(undefined);
isEmailApplyEligibleAtom.debugLabel = 'isEmailApplyEligibleAtom';
export const isEmailValidatedAtom = atom<boolean | undefined>(undefined);
isEmailValidatedAtom.debugLabel = 'isEmailValidatedAtom';
export const hasVerifiedIdentityAtom = atom<boolean | undefined>(undefined);
hasVerifiedIdentityAtom.debugLabel = 'hasVerifiedIdentityAtom';
export const ssnMatchedAtom = atom<boolean | undefined>(undefined);
ssnMatchedAtom.debugLabel = 'ssnMatchedAtom';
export const archivalEligibleAtom = atom<boolean | undefined>(undefined);
archivalEligibleAtom.debugLabel = 'archivalEligibleAtom';

export const registrationDataAtom = atom<Partial<RegistrationDataT>>({});
registrationDataAtom.debugLabel = 'registrationDataAtom';

export const incomeDataAtom = atom<IncomeDataT>({});
incomeDataAtom.debugLabel = 'incomeDataAtom';

export const doesServerHaveIncomeDataAtom = atom<boolean>(false);
doesServerHaveIncomeDataAtom.debugLabel = 'doesServerHaveIncomeDataAtom';

export const applyEligibilityAtom = atom<ApplyEligibilityT | undefined>(undefined);
applyEligibilityAtom.debugLabel = 'applyEligibilityAtom';

export const isIncomeDetailsRequiredAtom = atom<boolean>(false);
isIncomeDetailsRequiredAtom.debugLabel = 'isIncomeDetailsRequiredAtom';

export const iovationBlackboxAtom = atom<IovationBlackboxT>({ blackbox: undefined, isComplete: false });
iovationBlackboxAtom.debugLabel = 'iovationBlackboxAtom';

export const iovationPurchasedAtom = atom<boolean>(false);
iovationPurchasedAtom.debugLabel = 'iovationPurchasedAtom';

export const sendCodeCountdownSecondsAtom = atom<number>(SEND_CODE_COUNTDOWN_SECONDS);
sendCodeCountdownSecondsAtom.debugLabel = 'sendCodeCountdownSecondsAtom';

export const appVariantAtom = atom<'dashboard' | 'apply'>('dashboard');
appVariantAtom.debugLabel = 'appVariantAtom';

export const isApplyVariantAtom = atom<boolean>((get) => get(appVariantAtom) === 'apply');
isApplyVariantAtom.debugLabel = 'isApplyVariantAtom';

export const isCaliforniaAddressAtom = atom<boolean>((get) => {
  const { address } = get(registrationDataAtom);

  return address?.state === 'CA';
});
isCaliforniaAddressAtom.debugLabel = 'isCaliforniaAddressAtom';

export const isWisconsinAddressAtom = atom<boolean>((get) => {
  const { address } = get(registrationDataAtom);

  return address?.state === 'WI';
});
isWisconsinAddressAtom.debugLabel = 'isWisconsinAddressAtom';
